import React, { useEffect, useRef } from "react";

/**
 * The tab's backdrop: a drifting nebula, a canvas starfield, and the vignette
 * that sinks both into the page. Purely decorative — nothing here reads data,
 * and it is `aria-hidden` from top to bottom.
 *
 * ABSOLUTE, NOT FIXED. The mockup pins its backdrop to the viewport; inside the
 * app shell that would paint over the sidebar and the header, so the layers are
 * absolute within `.sn-root` instead (see supernova.css).
 *
 * REDUCED MOTION. `prefers-reduced-motion: reduce` stops the CSS animations via
 * the media query in supernova.css, and stops this canvas here: the stars are
 * painted once at their resting brightness and the animation loop never starts.
 * A still starfield is the same picture, minus the twinkle.
 *
 * DEFENSIVE ABOUT ITS CANVAS. The tab's tests render this component for real,
 * and jsdom has no 2D canvas: asking it for one logs a "not implemented" error
 * and hands back null. So we check that the environment HAS a 2D context type
 * before asking for one, and check the result again afterwards.
 */

/* The mockup's 340 stars over a 1440×1000 viewport, expressed as a density so
   a long tab gets the same sky rather than a thinner one. Capped, because the
   loop repaints every star every frame. */
const STAR_DENSITY = 340 / (1440 * 1000);
const MAX_STARS = 1100;

/* The canvas spans the whole scrolling tab, and the tab is tall — a 390px phone
   runs to several thousand pixels. Past this many device pixels the bitmap is
   rendered smaller than its box and stretched back up: a ceiling on both the
   memory it holds and the area cleared every frame. Scaled uniformly, so the
   stars stay round rather than becoming ellipses. */
const MAX_CANVAS_PX = 1440 * 3200;

/* Mostly white, with the occasional gold and violet one — the mockup's every
   11th and every 17th star. */
const starColor = (i) =>
  i % 11 === 0 ? "245,227,174" : i % 17 === 0 ? "170,140,255" : "255,255,255";

/**
 * A deterministic pseudo-random sequence. `Math.random()` would re-scatter the
 * field on every resize, which reads as a flicker rather than a starfield.
 */
const scatter = (seed) => {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
};

/**
 * `width`/`height` are bitmap pixels; `scale` is how much smaller that bitmap is
 * than the box it gets stretched over. Density is counted against the displayed
 * area, not the bitmap, so downscaling a very tall canvas thins the pixels
 * without thinning the sky.
 */
const buildStars = (width, height, scale) => {
  const rand = scatter(20260808);
  const displayed = (width * height) / (scale * scale);
  const count = Math.min(MAX_STARS, Math.round(displayed * STAR_DENSITY));
  const stars = [];
  for (let i = 0; i < count; i += 1) {
    stars.push({
      x: rand() * width,
      y: rand() * height,
      r: (rand() * 1.5 + 0.2) * scale,
      phase: rand() * 6.3,
      speed: rand() * 0.02 + 0.004,
      color: starColor(i),
    });
  }
  return stars;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SupernovaBackdrop = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") return undefined;
    /* jsdom defines the element but not the context type. Asking anyway would
       print a "not implemented" error into every test run for no gain. */
    if (typeof window.CanvasRenderingContext2D === "undefined") return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined; // a browser that refused the context

    const still = prefersReducedMotion();
    let stars = [];
    let frame = 0;
    let raf = null;

    const paint = (tick) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach((star) => {
        /* At rest (reduced motion) every star sits at the middle of its own
           twinkle rather than at the top of it — a field of stars frozen at
           full brightness reads as glare. */
        const alpha = still ? 0.4 : 0.25 + Math.sin(tick * star.speed + star.phase) * 0.45;
        ctx.globalAlpha = Math.max(0.05, alpha);
        ctx.fillStyle = `rgb(${star.color})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, 7);
        ctx.fill();
        if (star.r > 1.2) {
          ctx.globalAlpha = alpha * 0.16;
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.r * 4.5, 0, 7);
          ctx.fill();
        }
      });
    };

    const loop = () => {
      frame += 1;
      paint(frame);
      raf = window.requestAnimationFrame(loop);
    };

    const resize = () => {
      /* The canvas fills `.sn-root`, which is the tab — not the window, and not
         the first screen of it either. */
      const { width, height } = canvas.getBoundingClientRect();
      if (!width || !height) return;

      const scale = Math.min(1, Math.sqrt(MAX_CANVAS_PX / (width * height)));
      /* Floored, not rounded: rounding both dimensions up can carry the bitmap
         back over the ceiling it was scaled to fit under. */
      const w = Math.max(1, Math.floor(width * scale));
      const h = Math.max(1, Math.floor(height * scale));
      /* Reassigning either dimension wipes the bitmap, so a no-op resize would
         blank the sky for a frame. The observer below fires more often than the
         size actually changes. */
      if (w === canvas.width && h === canvas.height && stars.length) return;

      canvas.width = w;
      canvas.height = h;
      stars = buildStars(w, h, scale);
      if (still) paint(0);
    };

    resize();
    if (!still && typeof window.requestAnimationFrame === "function") loop();
    window.addEventListener("resize", resize);

    /* THE TAB GROWS AFTER MOUNT. Chain reads land, cards and stake rows render,
       and `.sn-root` gets taller — without this the canvas would keep the
       bitmap it measured while the tab was still a column of skeletons, and the
       sky would stretch instead of extending. Absent in jsdom, hence the guard. */
    let observer = null;
    if (typeof window.ResizeObserver === "function") {
      observer = new window.ResizeObserver(resize);
      observer.observe(canvas);
    }

    return () => {
      window.removeEventListener("resize", resize);
      if (observer) observer.disconnect();
      if (raf !== null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(raf);
      }
    };
  }, []);

  return (
    <div aria-hidden="true" data-testid="supernova-backdrop">
      <div className="sn-neb" />
      <canvas ref={canvasRef} className="sn-sky" />
      <div className="sn-vig" />
    </div>
  );
};

export default SupernovaBackdrop;
